import { useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Checkbox from "../../components/common/Checkbox";
import Alert from "../../components/common/Alert";
import Modal from "../../components/common/Modal";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import Table from "../../components/common/Table";
import ActionButton from "../../components/common/ActionButton";
import ActionCheckbox from "../../components/common/ActionCheckbox";
import { useStore } from "../../context/StoreContext";
import useClientPagination from "../../hooks/useClientPagination";
import PromotionRulesEditor from "./PromotionRulesEditor";
import {
  useGetPromotionsQuery,
  useCreatePromotionMutation,
  useUpdatePromotionMutation,
  useDeletePromotionMutation,
} from "../../store/api";

const initialForm = {
  name: "",
  promoCode: "",
  promoType: "Sale",
  status: "Active",
  priority: 0,
  stackingPolicy: "Allow",
  maxUsesTotal: "",
  maxUsesPerCustomer: "",
  startsAt: "",
  endsAt: "",
  applyChannelPos: true,
  applyChannelQr: false,
  applyChannelKiosk: false,
  applyChannelOnline: false,
  applyDineIn: true,
  applyTakeaway: true,
  applyDelivery: false,
  isActive: true,
};

const dateInputValue = (value) => (value ? String(value).slice(0, 16) : "");
const optionalNumber = (value) =>
  value === "" || value === null || value === undefined ? null : Number(value);
const optionalUtcDate = (value) => (value ? new Date(value).toISOString() : null);

const toForm = (promotion = {}) => ({
  ...initialForm,
  name: promotion.name || promotion.promotionName || "",
  promoCode: promotion.promoCode || promotion.code || "",
  promoType: promotion.promoType || "Sale",
  status: promotion.status || "Active",
  priority: promotion.priority ?? 0,
  stackingPolicy: promotion.stackingPolicy || "Allow",
  maxUsesTotal: promotion.maxUsesTotal ?? "",
  maxUsesPerCustomer: promotion.maxUsesPerCustomer ?? "",
  startsAt: dateInputValue(promotion.startsAt),
  endsAt: dateInputValue(promotion.endsAt),
  applyChannelPos: promotion.applyChannelPos ?? true,
  applyChannelQr: promotion.applyChannelQr ?? false,
  applyChannelKiosk: promotion.applyChannelKiosk ?? false,
  applyChannelOnline: promotion.applyChannelOnline ?? false,
  applyDineIn: promotion.applyDineIn ?? true,
  applyTakeaway: promotion.applyTakeaway ?? true,
  applyDelivery: promotion.applyDelivery ?? false,
  isActive: promotion.isActive ?? true,
});

const toPayload = (form) => ({
  name: form.name.trim(),
  promoCode: form.promoCode.trim() || null,
  promoType: form.promoType,
  status: form.status,
  priority: Number(form.priority) || 0,
  stackingPolicy: form.stackingPolicy,
  maxUsesTotal: optionalNumber(form.maxUsesTotal),
  maxUsesPerCustomer: optionalNumber(form.maxUsesPerCustomer),
  startsAt: optionalUtcDate(form.startsAt),
  endsAt: optionalUtcDate(form.endsAt),
  applyChannelPos: form.applyChannelPos,
  applyChannelQr: form.applyChannelQr,
  applyChannelKiosk: form.applyChannelKiosk,
  applyChannelOnline: form.applyChannelOnline,
  applyDineIn: form.applyDineIn,
  applyTakeaway: form.applyTakeaway,
  applyDelivery: form.applyDelivery,
  isActive: form.isActive,
});

const PromotionForm = ({ form, setForm, onSubmit, onCancel, editing, state }) => {
  const change = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  };
  const hasChannel =
    form.applyChannelPos || form.applyChannelQr || form.applyChannelKiosk || form.applyChannelOnline;
  const hasOrderType = form.applyDineIn || form.applyTakeaway || form.applyDelivery;
  const datesValid = !form.startsAt || !form.endsAt || form.endsAt >= form.startsAt;

  return (
    <Card>
      <h2 className="mb-1 text-lg font-semibold text-slate-900">
        {editing ? "Edit promotion" : "Add promotion"}
      </h2>
      <p className="mb-5 text-sm text-slate-500">
        Save the promotion first, then configure its minimum spend, discount effect, and weekly schedule.
      </p>
      <form className="space-y-6" onSubmit={onSubmit}>
        <div className="grid gap-4 md:grid-cols-2">
          <Input label="Promotion name" name="name" value={form.name} onChange={change} required />
          <Input label="Promo code (optional)" name="promoCode" value={form.promoCode} onChange={change} helper="POS asks for this code when one is configured." />
          <Select label="Promotion type" name="promoType" value={form.promoType} onChange={change}>
            <option value="Sale">Sale</option>
            <option value="Coupon">Coupon</option>
            <option value="Loyalty">Loyalty</option>
            <option value="Bundle">Bundle</option>
          </Select>
          <Select label="Status" name="status" value={form.status} onChange={change}>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
            <option value="Scheduled">Scheduled</option>
          </Select>
          <Select
            label="Stacking policy"
            name="stackingPolicy"
            value={form.stackingPolicy}
            onChange={change}
            helper="Stored for policy; current POS application does not enforce stacking rules."
          >
            <option value="Allow">Allow with other promotions</option>
            <option value="Disallow">Disallow stacking</option>
            <option value="Restrict">Restrict to same type</option>
          </Select>
          <Input
            label="Priority"
            name="priority"
            type="number"
            value={form.priority}
            onChange={change}
            helper="Higher priority promotions appear first."
          />
          <Input
            label="Starts at"
            name="startsAt"
            type="datetime-local"
            value={form.startsAt}
            onChange={change}
          />
          <Input
            label="Ends at"
            name="endsAt"
            type="datetime-local"
            value={form.endsAt}
            onChange={change}
            error={!datesValid ? "End date must be after the start date." : undefined}
          />
          <Input
            label="Maximum total uses"
            name="maxUsesTotal"
            type="number"
            min="1"
            value={form.maxUsesTotal}
            onChange={change}
            helper="Stored for future redemption enforcement."
          />
          <Input
            label="Maximum uses per customer"
            name="maxUsesPerCustomer"
            type="number"
            min="1"
            value={form.maxUsesPerCustomer}
            onChange={change}
            helper="Stored for future redemption enforcement."
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <fieldset className="rounded-lg border border-slate-200 p-4">
            <legend className="px-2 text-sm font-semibold text-slate-800">Sales channels</legend>
            <div className="grid gap-3">
              <Checkbox label="POS" name="applyChannelPos" checked={form.applyChannelPos} onChange={change} />
              <Checkbox label="QR ordering" name="applyChannelQr" checked={form.applyChannelQr} onChange={change} />
              <Checkbox label="Kiosk" name="applyChannelKiosk" checked={form.applyChannelKiosk} onChange={change} />
              <Checkbox label="Online" name="applyChannelOnline" checked={form.applyChannelOnline} onChange={change} />
            </div>
          </fieldset>
          <fieldset className="rounded-lg border border-slate-200 p-4">
            <legend className="px-2 text-sm font-semibold text-slate-800">Order types</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <Checkbox label="Dine in" name="applyDineIn" checked={form.applyDineIn} onChange={change} />
              <Checkbox label="Takeaway" name="applyTakeaway" checked={form.applyTakeaway} onChange={change} />
              <Checkbox label="Delivery" name="applyDelivery" checked={form.applyDelivery} onChange={change} />
            </div>
          </fieldset>
        </div>

        <Checkbox label="Promotion active" name="isActive" checked={form.isActive} onChange={change} />
        {!hasChannel && <Alert tone="warning">Select at least one sales channel.</Alert>}
        {!hasOrderType && <Alert tone="warning">Select at least one order type.</Alert>}
        {state.isError && <Alert tone="error">{state.error?.data?.detail || "Failed to save promotion."}</Alert>}
        <div className="flex gap-3">
          <Button type="submit" disabled={state.isLoading || !form.name.trim() || !hasChannel || !hasOrderType || !datesValid}>
            {editing ? "Update promotion" : "Create promotion"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
};

const Promotions = () => {
  const { selectedStoreId } = useStore();
  const { data, isLoading, isError, error } = useGetPromotionsQuery(selectedStoreId, { skip: !selectedStoreId });
  const [createPromotion, createState] = useCreatePromotionMutation();
  const [updatePromotion, updateState] = useUpdatePromotionMutation();
  const [deletePromotion, deleteState] = useDeletePromotionMutation();
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [rulesPromotionId, setRulesPromotionId] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, promotionId: null, promotionName: "" });

  const promotions = Array.isArray(data) ? data : [];
  const pagination = useClientPagination(promotions, { pageSize: 10 });

  const resetForm = () => {
    setForm(initialForm);
    setEditingId(null);
    setShowForm(false);
  };

  const save = async (event) => {
    event.preventDefault();
    try {
      if (editingId) {
        await updatePromotion({ storeId: selectedStoreId, promotionId: editingId, payload: toPayload(form) }).unwrap();
      } else {
        await createPromotion({ storeId: selectedStoreId, payload: toPayload(form) }).unwrap();
      }
      resetForm();
    } catch {
      // Mutation error is rendered by the form.
    }
  };

  const toggleActive = async (promotion) => {
    const next = toForm(promotion);
    next.isActive = !next.isActive;
    next.status = next.isActive ? "Active" : "Inactive";
    await updatePromotion({
      storeId: selectedStoreId,
      promotionId: promotion.id || promotion.promotionId,
      payload: toPayload(next),
    });
  };

  const confirmDelete = async () => {
    try {
      await deletePromotion({ storeId: selectedStoreId, promotionId: deleteModal.promotionId }).unwrap();
    } finally {
      setDeleteModal({ isOpen: false, promotionId: null, promotionName: "" });
    }
  };

  if (!selectedStoreId) {
    return <div className="space-y-6"><PageHeader title="Promotions" subtitle="Manage promotional offers." /><Card><Alert tone="warning">Please select a store to manage promotions.</Alert></Card></div>;
  }

  if (rulesPromotionId) {
    return <PromotionRulesEditor storeId={selectedStoreId} promotionId={rulesPromotionId} onBack={() => setRulesPromotionId(null)} />;
  }

  const columns = [
    { key: "name", label: "Name" },
    { key: "promoCode", label: "Promo code" },
    { key: "channels", label: "Channels" },
    { key: "window", label: "Availability" },
    { key: "status", label: "Status" },
    { key: "actions", label: "Actions" },
  ];
  const rows = pagination.paginatedItems.map((promotion) => {
    const id = promotion.id || promotion.promotionId;
    const channels = [promotion.applyChannelPos && "POS", promotion.applyChannelQr && "QR", promotion.applyChannelKiosk && "Kiosk", promotion.applyChannelOnline && "Online"].filter(Boolean);
    return {
      id,
      name: <div><div className="font-semibold text-slate-900">{promotion.name}</div><div className="text-xs text-slate-500">{promotion.promoType} · Priority {promotion.priority ?? 0}</div></div>,
      promoCode: promotion.promoCode || "No code",
      channels: channels.join(", ") || "None",
      window: promotion.startsAt || promotion.endsAt ? `${promotion.startsAt ? new Date(promotion.startsAt).toLocaleDateString() : "Now"} – ${promotion.endsAt ? new Date(promotion.endsAt).toLocaleDateString() : "No end"}` : "Always",
      status: <div className="flex items-center gap-2"><ActionCheckbox checked={Boolean(promotion.isActive)} label={promotion.isActive ? "Active" : "Inactive"} onChange={() => toggleActive(promotion)} /><span className="text-sm text-slate-700">{promotion.isActive ? "Active" : "Inactive"}</span></div>,
      actions: <div className="flex flex-wrap items-center gap-3"><Button size="sm" variant="secondary" onClick={() => setRulesPromotionId(id)}>Rules</Button><ActionButton label="Edit" icon="edit" onClick={() => { setEditingId(id); setForm(toForm(promotion)); setShowForm(true); }} /><ActionButton label="Delete" icon="delete" tone="danger" onClick={() => setDeleteModal({ isOpen: true, promotionId: id, promotionName: promotion.name })} /></div>,
    };
  });

  return (
    <div className="space-y-6">
      <PageHeader title="Promotions" subtitle="Create promotions and configure the rules used by POS." actions={showForm ? <Button size="sm" variant="secondary" onClick={resetForm}>Back</Button> : <Button size="sm" onClick={() => { setForm(initialForm); setShowForm(true); }}>Add promotion</Button>} />
      {showForm ? (
        <PromotionForm form={form} setForm={setForm} onSubmit={save} onCancel={resetForm} editing={Boolean(editingId)} state={editingId ? updateState : createState} />
      ) : (
        <Card className="p-0">
          <div className="border-b border-slate-100 px-6 py-4 text-sm font-semibold text-slate-700">All promotions</div>
          <div className="px-6 py-4">
            {isLoading && <p className="text-sm text-slate-500">Loading promotions...</p>}
            {isError && <Alert tone="error">{error?.data?.detail || "Failed to load promotions."}</Alert>}
            {!isLoading && !isError && <><Table columns={columns} rows={rows} emptyText="No promotions yet." />{pagination.totalPages > 1 && <Pagination page={pagination.page} pageSize={pagination.pageSize} totalCount={pagination.totalCount} onPageChange={pagination.setPage} onPageSizeChange={pagination.setPageSize} />}</>}
          </div>
        </Card>
      )}
      <Modal isOpen={deleteModal.isOpen} onClose={() => setDeleteModal({ isOpen: false, promotionId: null, promotionName: "" })} title="Delete promotion" confirmText="Delete" onConfirm={confirmDelete} isLoading={deleteState.isLoading}>
        <p>Are you sure you want to delete <strong>{deleteModal.promotionName}</strong>?</p>
        {deleteState.isError && <Alert tone="error">{deleteState.error?.data?.detail || "Failed to delete promotion."}</Alert>}
      </Modal>
    </div>
  );
};

export default Promotions;
